import { type Market } from "@dotcom/markets";
import styled from "@emotion/styled";
import {
  Alignments,
  Colors,
  Column,
  Drawer,
  DrawerVariants,
  Grid,
  GridColumn,
  Heading,
  Opacities,
  Spacer,
  Spacings,
  createCustomSpacing,
  useFocusInitial,
  useFocusTrap,
  withOpacity,
  type IconButtonProps,
} from "@polestar/component-warehouse-react";
import { render as toPlainText } from "datocms-structured-text-to-plain-text";
import { isHeading } from "datocms-structured-text-utils";
import React, { useRef, type FC } from "react";
import {
  StructuredText,
  renderRule,
  type StructuredTextGraphQlResponse,
} from "react-datocms";

import useChangelogOpenContext from "../../hooks/useChangelogOpenContext";

import {
  defaultStructuredTextMarkRules,
  listItemRenderRule,
  listRenderRule,
  paragraphRenderRule,
} from "~/common/components/StructuredTextRules";
import { localizeDate } from "~/common/helpers/date";

interface Props {
  readonly title: string;
  readonly subtitle: string;
  readonly description: string;
  readonly changelog?: StructuredTextGraphQlResponse;
  readonly market: Market;
}

const StyledSubtitle = styled(Heading)`
  color: ${withOpacity(Colors.black, Opacities.medium)};
`;

const StyledTopSpacer = styled(Spacer)`
  &:nth-of-type(1) {
    height: 0;
  }
`;

const structuredTextRules = (market: Market) => {
  return [
    paragraphRenderRule,
    listRenderRule,
    listItemRenderRule,
    renderRule(isHeading, ({ node, key }) => {
      const dateRegex = /\[\d{4}-\d{2}-\d{2}\]/g;
      const bracketRegex = /\[|\]/g;

      const title = toPlainText(node);
      const date =
        title?.match(dateRegex)?.[0].replaceAll(bracketRegex, "") || "";

      const formattedDate = localizeDate(new Date(date), market);
      const formattedTitle = title?.replace(dateRegex, formattedDate);

      return (
        <React.Fragment key={key}>
          <StyledTopSpacer
            spacing={{
              mobile: createCustomSpacing(2.5),
              desktop: createCustomSpacing(3.5),
            }}
          />
          <Heading level={2}>{formattedTitle}</Heading>
          <Spacer spacing={Spacings.large} />
        </React.Fragment>
      );
    }),
  ];
};

const ChangelogDrawer: FC<Props> = ({
  title,
  subtitle,
  description,
  changelog,
  market,
}) => {
  const changelogOpenContext = useChangelogOpenContext();
  const containerRef = useRef<HTMLDivElement>(null);

  const isOpen = changelogOpenContext?.isOpen ?? false;
  useFocusTrap(containerRef, isOpen);
  useFocusInitial(containerRef, isOpen, { delayed: true });

  return (
    <Drawer
      ref={containerRef}
      withCloseButton
      autoFocus
      open={isOpen}
      closeButtonProps={
        {
          autoFocus: true,
          "aria-label": "Close",
        } as Omit<IconButtonProps, "icon">
      }
      variant={DrawerVariants.wide}
      position={Alignments.right}
      onClose={() => {
        changelogOpenContext?.setIsOpen(false);
      }}
    >
      <Grid>
        <GridColumn span={{ mobile: 4, tablet: 3, desktop: 4 }}>
          <StyledSubtitle level={2}>{subtitle}</StyledSubtitle>
          <Heading level={2}>{title}</Heading>
        </GridColumn>
        <GridColumn span={{ mobile: 4, tablet: 5, desktop: 6 }}>
          <Heading level={2}>{description}</Heading>
          <Spacer
            spacing={{
              mobile: Spacings.xxxLarge,
              desktop: Spacings.xxxxLarge,
            }}
          />
          <Column>
            <StructuredText
              data={changelog}
              customNodeRules={structuredTextRules(market)}
              customMarkRules={defaultStructuredTextMarkRules}
            />
          </Column>
        </GridColumn>
      </Grid>
    </Drawer>
  );
};

export default ChangelogDrawer;
